import React, { useRef, useState } from 'react';
import type { MjpegRelayArgs, MjpegRelayResult } from '../../services/multipartMjpegRelay';
import { Box, Typography } from '@mui/material';
import { SpatialOperationsLiveCamera } from '../spatialOperations/SpatialOperationsLiveCamera';
import {
  MIS_STICKY_ANNOTATED_CAMERA_ID,
  MIS_STICKY_ANNOTATED_DEVICE_ID,
  type SpatialOperationsLiveCameraView,
} from '../../hooks/useSpatialOperationsLiveCamera';
import { ShowcaseBadge } from './ShowcaseShell';
import {
  SHOWCASE_ACTIVE_SENTENCE,
  SHOWCASE_CAMERA_STREAM_UNAVAILABLE,
  SHOWCASE_EVIDENCE_LABEL,
  SHOWCASE_FORKLIFT_PROCESSING_PROVEN,
  SHOWCASE_LABEL_FALLBACK,
  SHOWCASE_LABEL_LIVE,
  SHOWCASE_LABEL_PROCESSED_LIVE,
  SHOWCASE_LABEL_RECORDED_FOOTAGE,
  SHOWCASE_LABEL_STALE,
  SHOWCASE_LABEL_STATIC,
  SHOWCASE_LIVE_BOOTH_CAMERA_RATIO,
  SHOWCASE_LIVE_BOOTH_COLUMNS,
  SHOWCASE_LIVE_BOOTH_TYPE,
  SHOWCASE_LIVE_CAMERA_LABEL,
  SHOWCASE_STALE_CAMERA_LABEL,
  SHOWCASE_LIVE_EVIDENCE_UNAVAILABLE,
  forkliftVisitorLabel,
  nextShowcaseLastKnownGoodPolygon,
  resolveShowcaseBoothLiveOverlayPolygon,
  showcaseDwellClause,
  showcaseLiveBoothAreaName,
  showcaseStateLabel,
  SHOWCASE_LIVE_TIMING_LINES,
  showcaseVisitorSentence,
  type ShowcaseLastKnownGoodPolygon,
  type ShowcasePane,
} from '../../domain/showcase/showcasePresentation';
import {
  SHOWCASE_CONDITION_PROXIMITY_LABEL,
  SHOWCASE_CONDITION_RAE_LABEL,
  SHOWCASE_EVIDENCE_IMAGE_HEIGHT,
  SHOWCASE_HISTORY_BUILDING,
  SHOWCASE_HISTORY_LOADING,
  SHOWCASE_MEASUREMENT_LOADING,
  SHOWCASE_MEASUREMENT_UNAVAILABLE,
  SHOWCASE_PROX_EVIDENCE_LOADING,
  SHOWCASE_PROX_OBSERVED,
  SHOWCASE_PROX_SCOPE_COPY,
  SHOWCASE_RAE_OBSERVED,
  SHOWCASE_RAE_SCOPE_COPY,
  SHOWCASE_ROOM_B_CARD_PADDING,
  SHOWCASE_VIEW_SUPPORTING_EVIDENCE,
  SHOWCASE_ROOM_B_GRID_ROWS,
  SHOWCASE_ROOM_B_GRID_ROWS_SHORT,
  SHOWCASE_ROOM_B_LOWER_COLUMNS,
  SHOWCASE_ROOM_B_TYPE,
  SHOWCASE_ROOM_B_VIDEO_ASPECT,
  SHOWCASE_ROOM_B_VIDEO_MAX_WIDTH,
  SHOWCASE_ROOM_B_VIDEO_OBJECT_FIT,
  SHOWCASE_ROOM_B_ZONE_FILL,
  SHOWCASE_ROOM_B_ZONE_STROKE,
  SHOWCASE_ROOM_B_ZONE_STROKE_WIDTH_PX,
  SHOWCASE_SHORT_VIEWPORT,
  SHOWCASE_ROOM_B_SCENARIO,
  SHOWCASE_ROOM_B_SECONDARY,
  SHOWCASE_STORY_NOW,
  SHOWCASE_RECORDED_REPLAY_PATTERN,
  SHOWCASE_STORY_REPEATING,
  SHOWCASE_STORY_REPEATING_RECORDED,
  SHOWCASE_SUPPORTING_EVIDENCE_EMPTY,
  SHOWCASE_TYPE,
  showcaseLastCompletedLine,
  showcaseMostFrequentLabel,
  showcaseRoomBAvailability,
  showcaseSupportingEvidenceTitle,
  showcaseRepeatingRateLine,
  showcaseRepeatingWindowId,
  showcaseRepeatingWindowLabel,
  type ShowcaseConditionBundle,
  type ShowcaseConditionPresentation,
} from '../../domain/showcase/showcaseConditionData';
import {
  SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS,
  type ShowcaseRecentEvidenceView,
} from '../../domain/showcase/showcaseRecentEvidence';
import { isBoothRecentEvidenceEnabled } from '../../domain/showcase/boothRecentEvidence';
import { showcaseTrustFor } from '../../domain/showcase/showcaseTrustPolicy';
import type { ShowcaseTrustSurface } from '../../domain/showcase/showcaseTrustPolicy';
import { SHOWCASE_BOOTH_STILL, SHOWCASE_FORKLIFT_STILL } from '../../domain/showcase/showcaseStills';
import { workflowReviewLabel, type ShowcaseLiveNowSnapshot, type ShowcaseLivePhase } from '../../domain/showcase/liveNowAdapter';
import { BoothManualSmsControl } from './BoothManualSmsControl';
import { ShowcaseQualifyingEntries } from './ShowcaseQualifyingEntries';
import { ShowcaseVerifyFooter } from './ShowcaseCompletedVerify';
import type { OverviewAreaGeometry } from '../../pages/OverviewAreaCameraPanel';
import type { OverTimeWindowId } from '../../domain/sellableSiteExperience/overTimePresenter';

const PHASE_COLOR: Record<ShowcaseLivePhase, string> = {
  clear: '#4ade80',
  observing: '#facc15',
  active: '#f87171',
  unknown: '#cbd5e1',
};

const cameraFillSx = {
  height: '100%',
  minHeight: 0,
  '& [data-testid="spatial-operations-live-camera"]': {
    height: '100%',
    minHeight: '0 !important',
    maxHeight: '100%',
    border: 0,
    bgcolor: 'transparent',
  },
  '& [data-testid="spatial-operations-live-stream"], & [data-testid="spatial-operations-live-poster"]': {
    height: '100% !important',
    minHeight: '0 !important',
    maxHeight: '100%',
  },
  '& [data-testid="spatial-operations-live-status"]': { display: 'none' },
} as const;

const roomBCameraFillSx = {
  ...cameraFillSx,
  '& img': {
    width: '100% !important',
    height: '100% !important',
    objectFit: `${SHOWCASE_ROOM_B_VIDEO_OBJECT_FIT} !important`,
    objectPosition: 'center center',
  },
  '& [data-testid="spatial-operations-zone-overlay-polygon"]': {
    fill: `${SHOWCASE_ROOM_B_ZONE_FILL} !important`,
    stroke: `${SHOWCASE_ROOM_B_ZONE_STROKE} !important`,
    strokeWidth: `${SHOWCASE_ROOM_B_ZONE_STROKE_WIDTH_PX}px`,
    vectorEffect: 'non-scaling-stroke',
  },
} as const;

const boothCameraFillSx = {
  ...cameraFillSx,
  '& img': {
    width: '100% !important',
    height: '100% !important',
    objectFit: 'contain !important',
    objectPosition: 'center center',
  },
} as const;

export type ShowcaseBoothPicture = 'original' | 'annotated';

export const SHOWCASE_ANNOTATED_UNAVAILABLE_ORIGINAL = 'Annotated unavailable — showing original';

function mjpegViewKind(mjpegUrl?: string | null): string | null {
  const query = String(mjpegUrl || '').split('?')[1] || '';
  return new URLSearchParams(query).get('view_kind');
}

/** True when the booth asked for annotated playback but the attached pixels are the original stream. */
export function showcaseShowingOriginalInsteadOfAnnotated(args: {
  boothPicture: ShowcaseBoothPicture;
  videoSource?: 'live' | 'annotated' | 'raw_fallback' | null;
  mjpegUrl?: string | null;
}): boolean {
  if (args.boothPicture !== 'annotated') return false;
  if (args.videoSource === 'raw_fallback') return true;
  if (args.videoSource === 'live' && args.mjpegUrl) return true;
  if (args.videoSource === 'annotated') {
    const kind = mjpegViewKind(args.mjpegUrl);
    return kind != null && kind !== 'annotated';
  }
  return false;
}

function BoothPictureToggle({
  value,
  onChange,
  displayedSource,
}: {
  value: ShowcaseBoothPicture;
  onChange?: (next: ShowcaseBoothPicture) => void;
  displayedSource?: 'original' | 'annotated' | null;
}) {
  const options: Array<{ id: ShowcaseBoothPicture; label: string }> = [
    { id: 'original', label: 'ORIGINAL' },
    { id: 'annotated', label: 'ANNOTATED' },
  ];
  return (
    <Box
      data-testid="showcase-booth-picture-toggle"
      role="group"
      aria-label="Live Booth picture"
      sx={{ display: 'inline-flex', alignItems: 'center', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 0.5, overflow: 'hidden' }}
    >
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <Box
            key={option.id}
            component="button"
            type="button"
            data-testid={`showcase-booth-picture-${option.id}`}
            aria-pressed={selected}
            onClick={() => onChange?.(option.id)}
            sx={{
              border: 0,
              cursor: 'pointer',
              px: 0.7,
              py: 0.15,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.5,
              lineHeight: 1.4,
              fontFamily: 'inherit',
              color: '#fff',
              bgcolor: selected && displayedSource === option.id ? '#12744a' : 'transparent',
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

function boothLiveOverlays(args: {
  polygon: number[][];
  zoneId?: string | null;
}): OverviewAreaGeometry[] {
  if (!args.polygon || args.polygon.length < 3) return [];
  return [{
    zoneId: args.zoneId || 'configured-zone',
    label: 'Monitored area',
    polygon: args.polygon as Array<[number, number]>,
    deployed: true,
    measurementScopeKind: 'ZONE_POLYGON',
  }];
}

function StoryHeading({
  id,
  title,
  question,
  compact = false,
  testId,
}: {
  id: 'now' | 'repeating' | 'verify' | 'recent_evidence';
  title: string;
  question?: string;
  compact?: boolean;
  testId?: string;
}) {
  return (
    <Box data-testid={testId || `showcase-section-${id}`} sx={{ mb: compact ? 0.08 : 0.4 }}>
      <Typography sx={{ fontWeight: 800, letterSpacing: 1.4, fontSize: compact ? SHOWCASE_ROOM_B_TYPE.section : SHOWCASE_TYPE.sectionHeading, color: '#9fb0c8', lineHeight: 1 }}>
        {title}
      </Typography>
      {question ? (
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#c5d2e4', mt: 0.1, lineHeight: 1.2 }}>
          {question}
        </Typography>
      ) : null}
    </Box>
  );
}

function Card({
  testId,
  children,
  compact = false,
  spacious = false,
  liveFocus = false,
}: {
  testId: string;
  children: React.ReactNode;
  compact?: boolean;
  spacious?: boolean;
  liveFocus?: boolean;
}) {
  return (
    <Box
      data-testid={testId}
      data-compact={compact ? 'true' : 'false'}
      data-simplified={spacious ? 'true' : 'false'}
      data-live-focus={liveFocus ? 'true' : 'false'}
      sx={{
        height: spacious || compact ? 'auto' : '100%',
        minHeight: 0,
        p: spacious ? SHOWCASE_ROOM_B_CARD_PADDING : liveFocus ? { xs: 2, lg: 2.5 } : compact ? 0.45 : 0.55,
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 1.25,
        bgcolor: 'rgba(15,27,45,0.72)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        alignSelf: spacious || compact ? 'start' : 'stretch',
        [SHOWCASE_SHORT_VIEWPORT]: { p: spacious ? '20px' : liveFocus ? 1.5 : compact ? 0.35 : 0.4 },
      }}
    >
      {children}
    </Box>
  );
}

function EvidenceFrame({
  children,
  badge,
  title,
  subtitle,
  titleTestId,
  subtitleTestId,
}: {
  children: React.ReactNode;
  badge: React.ReactNode;
  title?: string;
  subtitle?: string;
  titleTestId?: string;
  subtitleTestId?: string;
}) {
  return (
    <Box
      data-testid="showcase-evidence"
      data-role="supporting"
      sx={{ minWidth: 0, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        <Box
          data-testid="showcase-evidence-heading"
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.5, flex: '0 0 auto' }}
        >
          <Box>
            <Typography
              data-testid={titleTestId}
              sx={{ fontWeight: 800, letterSpacing: 1.4, fontSize: title ? SHOWCASE_TYPE.sectionHeading : SHOWCASE_TYPE.meta, color: title ? '#f8fafc' : '#9fb0c8', lineHeight: 1.1 }}
            >
              {title || SHOWCASE_EVIDENCE_LABEL}
            </Typography>
            {subtitle ? (
              <Typography data-testid={subtitleTestId} sx={{ fontWeight: 650, fontSize: 15, color: '#c5d2e4', mt: 0.15, lineHeight: 1.2 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end' }}>{badge}</Box>
        </Box>
        <Box data-testid="showcase-evidence-frame" sx={{ minHeight: 0, flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function StillEvidence({
  src,
  alt,
  overlay,
  note,
  objectFit = 'contain',
}: {
  src: string;
  alt: string;
  overlay?: boolean;
  note?: string;
  objectFit?: 'contain' | 'cover';
}) {
  return (
    <Box sx={{ minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{ width: '100%', height: '100%', objectFit, objectPosition: 'center center', display: 'block', bgcolor: '#0c1624' }}
        />
        {overlay ? (
          <Box
            data-testid="showcase-zone-overlay"
            sx={{
              position: 'absolute',
              left: '18%',
              top: '22%',
              width: '44%',
              height: '50%',
              border: '2px solid rgba(96,165,250,0.9)',
              bgcolor: 'rgba(29,78,216,0.16)',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </Box>
      {note ? (
        <Typography data-testid="showcase-evidence-note" sx={{ color: '#9fb0c8', fontSize: SHOWCASE_TYPE.meta, mt: 0.5 }}>
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

function LiveStateCard({
  phase,
  liveNow,
  boothScope,
  siteId,
  policyId,
  siteTimeZone,
  recentEvidenceEnabled,
}: {
  phase: ShowcaseLivePhase;
  liveNow?: ShowcaseLiveNowSnapshot | null;
  boothScope?: { deviceId?: string | null; cameraId?: string | null; zoneId?: string | null } | null;
  siteId?: string | null;
  policyId?: string | null;
  siteTimeZone?: string | null;
  recentEvidenceEnabled: boolean;
}) {
  const dwell = liveNow?.dwellElapsedSeconds ?? null;
  const sentence = showcaseVisitorSentence(phase, dwell);
  const areaName = showcaseLiveBoothAreaName(liveNow?.areaName);
  const workflowNote = workflowReviewLabel(liveNow?.workflowOpenCount || 0);
  return (
    <Card testId="showcase-condition-hero" liveFocus>
      <Box
        data-testid="showcase-live-booth-measure"
        sx={{
          height: '100%',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          columnGap: 1.5,
          rowGap: 1,
          alignItems: 'start',
          alignContent: 'start',
        }}
      >
      <Box sx={{ gridColumn: '1', gridRow: '1', minWidth: 0 }}>
      <StoryHeading id="now" title={SHOWCASE_STORY_NOW} />
      <Typography
        data-testid="showcase-live-condition"
        sx={{ fontWeight: 800, letterSpacing: -0.4, fontSize: SHOWCASE_LIVE_BOOTH_TYPE.condition, mt: 0.75, lineHeight: 1.15 }}
      >
        {SHOWCASE_CONDITION_RAE_LABEL}
      </Typography>
      {areaName ? (
        <Typography
          data-testid="showcase-live-area"
          sx={{ fontWeight: 650, fontSize: SHOWCASE_LIVE_BOOTH_TYPE.supporting, color: '#c5d2e4', mt: 0.35, lineHeight: 1.25 }}
        >
          {areaName}
        </Typography>
      ) : null}
      <Typography
        data-testid="showcase-live-phase"
        sx={{
          fontWeight: 800,
          fontSize: { xs: 32, lg: 40 },
          letterSpacing: -1.2,
          lineHeight: 0.92,
          mt: 1.5,
          color: PHASE_COLOR[phase],
        }}
      >
        {showcaseStateLabel(phase)}
      </Typography>
      {workflowNote ? (
        <Typography
          data-testid="showcase-workflow-case"
          sx={{ fontWeight: 650, fontSize: SHOWCASE_LIVE_BOOTH_TYPE.supporting, color: '#c5d2e4', mt: 0.75, lineHeight: 1.25 }}
        >
          {workflowNote}
        </Typography>
      ) : null}
      <Typography
        data-testid="showcase-live-detail"
        sx={{ fontWeight: 650, fontSize: SHOWCASE_LIVE_BOOTH_TYPE.supporting, mt: 0.6, lineHeight: 1.2 }}
      >
        {sentence}
      </Typography>
      <Typography data-testid="showcase-live-dwell" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999 }}>
        {showcaseDwellClause(dwell) || '—'}
      </Typography>
      <Typography data-testid="showcase-live-state" data-phase={phase} sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999 }}>
        {SHOWCASE_ACTIVE_SENTENCE}
      </Typography>
      </Box>
      <ShowcaseQualifyingEntries
        enabled
        recentEvidenceEnabled={recentEvidenceEnabled}
        matchingIncidentId={liveNow?.matchingIncidentId}
        siteId={siteId}
        conditionId={liveNow?.conditionId}
        deviceId={boothScope?.deviceId || null}
        cameraId={boothScope?.cameraId || liveNow?.cameraId || null}
        zoneId={boothScope?.zoneId || liveNow?.zoneId || null}
        policyId={policyId}
        timeZone={siteTimeZone}
      />
      <Box data-testid="showcase-live-timing" data-legend="compact" sx={{ gridColumn: '1 / -1', mt: 0.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {SHOWCASE_LIVE_TIMING_LINES.map((line) => (
          <Typography
            key={line.id}
            data-testid={`showcase-live-timing-${line.id}`}
            sx={{ color: '#9fb0c8', fontSize: 13, lineHeight: 1.2 }}
          >
            <Box component="span" sx={{ fontWeight: 750, color: '#c5d2e4' }}>{line.label}</Box>
            {' '}
            {line.text}
          </Typography>
        ))}
      </Box>
      <Box
        data-testid="showcase-live-booth-sms"
        data-placement="bottom-right"
        sx={{ gridColumn: '1 / -1', pt: 1, display: 'flex', justifyContent: 'flex-end' }}
      >
        <BoothManualSmsControl
          deviceId={boothScope?.deviceId || null}
          cameraId={boothScope?.cameraId || liveNow?.cameraId || null}
          zoneId={boothScope?.zoneId || liveNow?.zoneId || null}
        />
      </Box>
      </Box>
    </Card>
  );
}

function qualitativeState(
  condition: ShowcaseConditionPresentation | null,
  status: 'idle' | 'loading' | 'ready' | 'unavailable',
): string {
  if ((status === 'idle' || status === 'loading') && !condition) return SHOWCASE_MEASUREMENT_LOADING;
  return condition?.qualitativeValue || 'Current activity unavailable';
}

function RoomBRecentEvidence({
  evidence,
  incidentType,
  caption,
  testId,
  imgAlt,
  title,
  open,
  onClose,
}: {
  evidence: ShowcaseRecentEvidenceView;
  incidentType: string;
  caption: string;
  testId: string;
  imgAlt: string;
  title: string;
  open: boolean;
  onClose?: () => void;
}) {
  const readyCount = Math.min(SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS, evidence.items.length);
  return (
    <Box
      data-testid={testId}
      data-status={evidence.status}
      data-ready-count={String(readyCount)}
      data-max-items={String(SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS)}
      data-incident-type={incidentType}
      data-expanded={open ? 'true' : 'false'}
      data-layer="video-overlay"
      sx={{
        display: open ? 'block' : 'none',
        position: 'absolute',
        left: 8,
        right: 8,
        bottom: 8,
        zIndex: 6,
        bgcolor: 'rgba(7,16,24,0.94)',
        border: '1px solid rgba(255,255,255,0.16)',
        borderRadius: 1,
        p: 1,
        pointerEvents: 'auto',
      }}
    >
      {open ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
            <StoryHeading
              id="recent_evidence"
              title={title}
              compact
              testId={`${testId}-heading`}
            />
            <Box
              component="button"
              type="button"
              data-testid={`${testId}-close`}
              onClick={onClose}
              sx={{
                border: 0,
                bgcolor: 'transparent',
                color: '#9fb0c8',
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                p: 0,
              }}
            >
              Close
            </Box>
          </Box>
          {evidence.items.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${readyCount}, minmax(0, 1fr))`,
                gap: 0.5,
                mt: 0.25,
              }}
            >
              {evidence.items.slice(0, SHOWCASE_RECENT_EVIDENCE_MAX_ITEMS).map((item, index) => (
                <Box key={`${item.imageUrl}-${index}`} data-testid={`${testId}-item-${index}`}>
                  <Box
                    component="img"
                    data-testid={`${testId}-img-${index}`}
                    src={item.imageUrl}
                    alt={imgAlt}
                    sx={{
                      width: '100%',
                      height: SHOWCASE_EVIDENCE_IMAGE_HEIGHT,
                      objectFit: 'cover',
                      display: 'block',
                      bgcolor: '#0c1624',
                      borderRadius: 0.5,
                      [SHOWCASE_SHORT_VIEWPORT]: { height: 'clamp(44px, 5vh, 56px)' },
                    }}
                  />
                  <Typography data-testid={`${testId}-time-${index}`} sx={{ fontSize: SHOWCASE_TYPE.meta, mt: 0.08, fontWeight: 700, lineHeight: 1.1 }}>
                    {item.timeLabel}
                  </Typography>
                  <Typography data-testid={`${testId}-label-${index}`} sx={{ fontSize: SHOWCASE_TYPE.meta, color: '#9fb0c8', lineHeight: 1.1 }}>
                    {caption}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : evidence.status === 'loading' || evidence.status === 'idle' ? (
            <Typography data-testid={`${testId}-loading`} sx={{ fontSize: SHOWCASE_TYPE.meta }}>
              {SHOWCASE_PROX_EVIDENCE_LOADING}
            </Typography>
          ) : (
            <Typography data-testid={`${testId}-empty`} sx={{ fontSize: SHOWCASE_TYPE.meta, color: '#9fb0c8' }}>
              {SHOWCASE_SUPPORTING_EVIDENCE_EMPTY}
            </Typography>
          )}
        </>
      ) : null}
    </Box>
  );
}

function RoomBConditionCard({
  testId,
  title,
  titleTestId,
  headlineTestId,
  scopeCopy,
  scopeTestId,
  condition,
  trust,
  status,
  selectedWindowId,
  repeatingTestIdPrefix,
  lastCompletedTestId,
  qualitativeTestId,
  evidenceTestId,
  onViewEvidence,
  recordedReplay = false,
}: {
  testId: string;
  title: string;
  titleTestId: string;
  headlineTestId?: string;
  scopeCopy: string;
  scopeTestId: string;
  condition: ShowcaseConditionPresentation | null;
  trust: ShowcaseTrustSurface;
  status: 'idle' | 'loading' | 'ready' | 'unavailable';
  selectedWindowId?: OverTimeWindowId;
  repeatingTestIdPrefix: string;
  lastCompletedTestId: string;
  qualitativeTestId: string;
  evidenceTestId: string;
  onViewEvidence: () => void;
  /** Warehouse Demo recorded/replay source. Room B is that source. */
  recordedReplay?: boolean;
}) {
  const availability = showcaseRoomBAvailability({ status, condition });
  const awaitingFirstLoad = (status === 'idle' || status === 'loading') && !condition;
  const qualitative = qualitativeState(condition, status);
  const lastCompletedLine = showcaseLastCompletedLine(condition?.lastCompletedValue);
  const peakLabel = recordedReplay
    ? null
    : showcaseMostFrequentLabel(condition?.recurring?.peak?.label);
  const recordedPattern = recordedReplay ? SHOWCASE_RECORDED_REPLAY_PATTERN : null;
  const rateLine = showcaseRepeatingRateLine(condition?.recurring);
  const repeatingReady = Boolean(condition?.recurring && (peakLabel || recordedPattern || rateLine));
  const waitingForRepeating = (status === 'idle' || status === 'loading') && !condition?.recurring;
  const repeatingWindowId = showcaseRepeatingWindowId({
    selectedWindowId,
    recurringWindowId: condition?.recurring?.windowId,
  });
  const repeatingWindowLabel = showcaseRepeatingWindowLabel(repeatingWindowId);
  const sectionDividerSx = {
    mt: 1.15,
    pt: 1.1,
    borderTop: '1px solid rgba(255,255,255,0.10)',
    flex: '0 0 auto',
  } as const;
  return (
    <Card testId={testId} spacious>
      <Box
        data-testid={testId.replace('-card', '-structure')}
        data-structure="NOW|REPEATING"
        sx={{ display: 'flex', flexDirection: 'column', height: 'auto', minHeight: 0 }}
      >
        <Typography
          data-testid={titleTestId}
          sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.title, letterSpacing: -0.4, lineHeight: 1.15, flex: '0 0 auto' }}
        >
          {title}
        </Typography>
        <Typography data-testid={scopeTestId} sx={{ color: '#9fb0c8', fontSize: SHOWCASE_ROOM_B_TYPE.subtitle, mt: 0.2, lineHeight: 1.25, flex: '0 0 auto' }}>
          {scopeCopy}
        </Typography>
        {headlineTestId ? (
          <Typography data-testid={headlineTestId} sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999 }}>
            {title}
          </Typography>
        ) : null}

        <Box sx={{ mt: 1.15, flex: '0 0 auto' }}>
          <StoryHeading
            id="now"
            title={SHOWCASE_STORY_NOW}
            compact
            testId={`${testId.replace('-card', '')}-section-now`}
          />
          {awaitingFirstLoad ? (
            <Typography data-testid={`${qualitativeTestId}-loading`} sx={{ fontWeight: 750, fontSize: SHOWCASE_ROOM_B_TYPE.now, lineHeight: 1.15 }}>
              {SHOWCASE_MEASUREMENT_LOADING}
            </Typography>
          ) : availability.nowUnavailable ? (
            <Typography data-testid={`${testId.replace('-card', '')}-unavailable`} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.now }}>
              {SHOWCASE_MEASUREMENT_UNAVAILABLE}
            </Typography>
          ) : (
            <>
              {trust.qualitativeNow ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                  <Box
                    data-testid={`${qualitativeTestId}-dot`}
                    sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#facc15', flex: '0 0 auto' }}
                  />
                  <Typography data-testid={qualitativeTestId} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.now, lineHeight: 1.15 }}>
                    {qualitative}
                  </Typography>
                </Box>
              ) : null}
              {trust.lastCompleted ? (
                <Box data-testid={lastCompletedTestId} sx={{ mt: 0.25 }}>
                  <Typography data-testid={`${lastCompletedTestId}-value`} sx={{ color: '#9fb0c8', fontSize: SHOWCASE_ROOM_B_TYPE.supporting, fontWeight: 650, lineHeight: 1.3 }}>
                    {lastCompletedLine}
                  </Typography>
                  {condition?.freshnessNote ? (
                    <Typography data-testid={`${lastCompletedTestId}-freshness`} sx={{ color: '#9fb0c8', fontSize: SHOWCASE_ROOM_B_TYPE.supporting, fontWeight: 650, lineHeight: 1.3 }}>
                      {condition.freshnessNote}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
            </>
          )}
        </Box>

        <Box sx={sectionDividerSx}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 0.15 }}>
            <StoryHeading
              id="repeating"
              title={recordedReplay ? SHOWCASE_STORY_REPEATING_RECORDED : SHOWCASE_STORY_REPEATING}
              compact
              testId={repeatingTestIdPrefix === 'showcase-repeating' ? 'showcase-section-repeating' : `${repeatingTestIdPrefix}-section`}
            />
            {repeatingWindowLabel && repeatingWindowId ? (
              <Typography
                data-testid={`${repeatingTestIdPrefix}-window-${repeatingWindowId}`}
                sx={{ color: '#9fb0c8', fontSize: SHOWCASE_ROOM_B_TYPE.section, fontWeight: 650, lineHeight: 1.2, whiteSpace: 'nowrap' }}
              >
                {repeatingWindowLabel}
              </Typography>
            ) : null}
          </Box>
          {trust.recurring ? (
            <Box
              {...(repeatingTestIdPrefix === 'showcase-room-b-prox-repeating'
                ? { 'data-testid': 'showcase-room-b-prox-recurring' }
                : {})}
              sx={{ mt: 0.15 }}
            >
              <Box data-testid={repeatingTestIdPrefix} data-simplified="true" data-real-graph="false">
                {waitingForRepeating ? (
                  <Typography data-testid={`${repeatingTestIdPrefix}-loading`} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.peak }}>
                    {SHOWCASE_HISTORY_LOADING}
                  </Typography>
                ) : availability.repeatingUnavailable ? (
                  <Typography data-testid={`${repeatingTestIdPrefix}-unavailable`} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.peak }}>
                    {SHOWCASE_MEASUREMENT_UNAVAILABLE}
                  </Typography>
                ) : !repeatingReady ? (
                  <Typography data-testid={`${repeatingTestIdPrefix}-building`} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.peak }}>
                    {SHOWCASE_HISTORY_BUILDING}
                  </Typography>
                ) : (
                  <>
                    {recordedPattern ? (
                      <Typography data-testid={`${repeatingTestIdPrefix}-peak`} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.peak, lineHeight: 1.2 }}>
                        {recordedPattern}
                      </Typography>
                    ) : peakLabel ? (
                      <Typography data-testid={`${repeatingTestIdPrefix}-peak`} sx={{ fontWeight: 800, fontSize: SHOWCASE_ROOM_B_TYPE.peak, lineHeight: 1.2 }}>
                        {peakLabel}
                      </Typography>
                    ) : null}
                    {rateLine ? (
                      <Typography data-testid={`${repeatingTestIdPrefix}-rate`} sx={{ color: '#9fb0c8', fontSize: SHOWCASE_ROOM_B_TYPE.supporting, fontWeight: 650, mt: 0.15, lineHeight: 1.3 }}>
                        {rateLine}
                      </Typography>
                    ) : null}
                  </>
                )}
                <Box
                  component="button"
                  type="button"
                  data-testid={`${evidenceTestId}-toggle`}
                  onClick={onViewEvidence}
                  sx={{
                    display: 'block',
                    border: 0,
                    bgcolor: 'transparent',
                    color: '#60a5fa',
                    p: 0,
                    mt: 0.7,
                    fontWeight: 700,
                    fontSize: SHOWCASE_ROOM_B_TYPE.supporting,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  {SHOWCASE_VIEW_SUPPORTING_EVIDENCE} →
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Card>
  );
}

export function ShowcaseNowPanes({
  pane,
  fallback,
  booth,
  forklift,
  liveNow,
  boothScope = null,
  siteId = null,
  policyId = null,
  siteTimeZone = null,
  forkliftZone,
  bundle = null,
  status = 'idle',
  selectedWindowId = 'rolling_24h',
  onSelectWindow: _onSelectWindow,
  lampConnected: _lampConnected = false,
  proximityEvidence = { status: 'empty', items: [] },
  raeEvidence = { status: 'empty', items: [] },
  boothPicture = 'annotated',
  onBoothPictureChange,
  boothStreamRelay,
}: {
  pane: ShowcasePane;
  fallback: boolean;
  booth: SpatialOperationsLiveCameraView;
  forklift: SpatialOperationsLiveCameraView;
  liveNow?: ShowcaseLiveNowSnapshot | null;
  boothScope?: { deviceId?: string | null; cameraId?: string | null; zoneId?: string | null } | null;
  siteId?: string | null;
  policyId?: string | null;
  siteTimeZone?: string | null;
  forkliftZone?: { cameraId?: string | null; zoneId?: string | null; polygon?: number[][] | null } | null;
  bundle?: ShowcaseConditionBundle | null;
  status?: 'idle' | 'loading' | 'ready' | 'unavailable';
  selectedWindowId?: OverTimeWindowId;
  onSelectWindow?: (windowId: OverTimeWindowId) => void;
  lampConnected?: boolean;
  proximityEvidence?: ShowcaseRecentEvidenceView;
  raeEvidence?: ShowcaseRecentEvidenceView;
  boothPicture?: ShowcaseBoothPicture;
  onBoothPictureChange?: (next: ShowcaseBoothPicture) => void;
  boothStreamRelay?: (args: MjpegRelayArgs) => Promise<MjpegRelayResult>;
}) {
  const [boothFreshFrame, setBoothFreshFrame] = useState(false);
  const [heldFrameStale, setHeldFrameStale] = useState(false);
  const boothFrameKey = `${booth.status}:${booth.mjpegUrl || ''}`;
  const boothFrameKeyRef = useRef(boothFrameKey);
  if (boothFrameKeyRef.current !== boothFrameKey) {
    boothFrameKeyRef.current = boothFrameKey;
    if (boothFreshFrame) setBoothFreshFrame(false);
  }
  const boothLiveBadge = !fallback && booth.status === 'live' && boothFreshFrame;
  const lastKnownGoodRef = useRef<ShowcaseLastKnownGoodPolygon | null>(null);
  const forkliftLastKnownGoodRef = useRef<ShowcaseLastKnownGoodPolygon | null>(null);
  const [openEvidence, setOpenEvidence] = useState<'rae' | 'prox' | null>(null);
  const recentEvidenceEnabled = isBoothRecentEvidenceEnabled(
    typeof window === 'undefined' ? '' : window.location.search,
  );
  const boothSelected = pane !== 'forklift';
  const boothLive = !fallback && booth.status === 'live';
  const misStickyAnnotatedSelected = boothPicture === 'annotated'
    && boothScope?.deviceId === MIS_STICKY_ANNOTATED_DEVICE_ID
    && boothScope?.cameraId === MIS_STICKY_ANNOTATED_CAMERA_ID;
  const boothCameraOn = !fallback && (
    booth.status === 'live'
    || booth.status === 'connecting'
    || booth.status === 'fallback_snapshot'
    || Boolean(booth.mjpegUrl)
  );
  const misAnnotatedUnavailable = (
    !fallback
    && misStickyAnnotatedSelected
    && !boothCameraOn
  );
  const forkliftVideoReady = !fallback && (
    Boolean(forklift.mjpegUrl)
    || forklift.status === 'live'
    || forklift.status === 'connecting'
    || forklift.status === 'fallback_snapshot'
  );
  const phase = liveNow?.phase || 'unknown';
  const recordedLabel = forkliftVisitorLabel(SHOWCASE_FORKLIFT_PROCESSING_PROVEN);
  const showProcessedLive = recordedLabel === SHOWCASE_LABEL_PROCESSED_LIVE || recordedLabel === 'RECORDED · PROCESSED LIVE';
  const roomBRae = bundle?.roomBRae || null;
  const roomBProximity = bundle?.roomBProximity || null;
  const raeTrust = showcaseTrustFor('room_b', 'rae');
  const proxTrust = showcaseTrustFor('room_b', 'proximity');
  lastKnownGoodRef.current = nextShowcaseLastKnownGoodPolygon({
    cameraId: liveNow?.cameraId,
    zoneId: liveNow?.zoneId,
    livePolygon: liveNow?.zonePolygon,
    previous: lastKnownGoodRef.current,
  });
  forkliftLastKnownGoodRef.current = nextShowcaseLastKnownGoodPolygon({
    cameraId: forkliftZone?.cameraId,
    zoneId: forkliftZone?.zoneId,
    livePolygon: forkliftZone?.polygon,
    previous: forkliftLastKnownGoodRef.current,
  });
  const showingOriginalInsteadOfAnnotated = showcaseShowingOriginalInsteadOfAnnotated({
    boothPicture,
    videoSource: booth.videoSource,
    mjpegUrl: booth.mjpegUrl,
  });
  // Selecting Original drops annotated burn-in immediately. videoSource stays
  // "annotated" until the live handshake finishes, so that stale value must not
  // hide the cloud polygon on the first Original paint.
  const originalSelected = boothPicture === 'original';
  const displayedVideoSource: 'original' | 'annotated' | null = originalSelected && (booth.mjpegUrl || boothCameraOn)
    ? 'original'
    : showingOriginalInsteadOfAnnotated
      ? 'original'
      : booth.videoSource === 'annotated' && Boolean(booth.mjpegUrl)
        ? 'annotated'
        : booth.mjpegUrl
          ? 'original'
          : null;
  const boothAnnotatedBurnIn = !originalSelected
    && booth.videoSource === 'annotated'
    && !showingOriginalInsteadOfAnnotated;
  const showBoothCloudOverlay = originalSelected ? boothCameraOn : boothLive && !boothAnnotatedBurnIn;
  const liveOverlayPolygon = showBoothCloudOverlay
    ? resolveShowcaseBoothLiveOverlayPolygon({
      livePolygon: liveNow?.zonePolygon,
      lastKnownGood: lastKnownGoodRef.current,
    })
    : [];
  const overlays = boothLiveOverlays({
    polygon: liveOverlayPolygon,
    zoneId: liveNow?.zoneId || lastKnownGoodRef.current?.zoneId,
  });
  const forkliftOverlayPolygon = forkliftVideoReady
    ? resolveShowcaseBoothLiveOverlayPolygon({
      livePolygon: forkliftZone?.polygon,
      lastKnownGood: forkliftLastKnownGoodRef.current,
    })
    : [];
  const forkliftOverlays = boothLiveOverlays({
    polygon: forkliftOverlayPolygon,
    zoneId: forkliftZone?.zoneId || forkliftLastKnownGoodRef.current?.zoneId,
  });

  return (
    <Box
      data-testid="showcase-now"
      data-layout="condition-hero"
      data-equal-panes="false"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, flex: 1, overflow: 'hidden' }}
    >
      {boothSelected ? (
        <Box
          data-testid="showcase-booth-pane"
          data-role="dominant"
          data-focused="true"
          data-repeating="false"
          data-verify="false"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            data-testid="showcase-live-booth-dashboard"
            data-camera-ratio={SHOWCASE_LIVE_BOOTH_CAMERA_RATIO}
            data-fit="contain"
            sx={{
              display: 'grid',
              gridTemplateColumns: SHOWCASE_LIVE_BOOTH_COLUMNS,
              gridTemplateRows: 'minmax(0, 1fr)',
              gap: 1.5,
              height: '100%',
              minHeight: 0,
              flex: 1,
            }}
          >
            <EvidenceFrame
              title={heldFrameStale ? SHOWCASE_STALE_CAMERA_LABEL : SHOWCASE_LIVE_CAMERA_LABEL}
              titleTestId="showcase-live-camera-label"
              badge={(
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {fallback ? (
                    <>
                      <ShowcaseBadge label={SHOWCASE_LABEL_STATIC} tone="fallback" testId="showcase-booth-badge-static" />
                      <ShowcaseBadge label={SHOWCASE_LABEL_FALLBACK} tone="fallback" testId="showcase-booth-badge-fallback" />
                    </>
                  ) : heldFrameStale ? (
                    <ShowcaseBadge label={SHOWCASE_LABEL_STALE} tone="fallback" testId="showcase-booth-badge-stale" />
                  ) : boothLiveBadge ? (
                    <ShowcaseBadge label={SHOWCASE_LABEL_LIVE} tone="live" testId="showcase-booth-badge-live" />
                  ) : boothCameraOn ? null : (
                    <ShowcaseBadge label={SHOWCASE_LABEL_STATIC} tone="fallback" testId="showcase-booth-badge-static" />
                  )}
                  {!fallback ? (
                    <BoothPictureToggle
                      value={boothPicture}
                      onChange={onBoothPictureChange}
                      displayedSource={displayedVideoSource}
                    />
                  ) : null}
                  {displayedVideoSource && displayedVideoSource !== boothPicture ? (
                    <Typography
                      data-testid="showcase-booth-displayed-source"
                      data-source={displayedVideoSource}
                      sx={{ color: '#e2e8f0', fontSize: 11, fontWeight: 800, letterSpacing: 0.4 }}
                    >
                      {displayedVideoSource === 'annotated' ? 'ANNOTATED' : 'ORIGINAL'}
                    </Typography>
                  ) : null}
                </Box>
              )}
            >
              {boothCameraOn ? (
                <Box
                  className="showcase-booth-video-slot"
                  data-testid="showcase-live-booth-video-frame"
                  data-fit="contain"
                  data-crop="false"
                  data-stretch="false"
                  data-object-fit="contain"
                  data-object-position="center center"
                  data-booth-picture={boothPicture}
                  data-booth-video-source={
                    showingOriginalInsteadOfAnnotated && booth.videoSource === 'annotated'
                      ? 'raw_fallback'
                      : (booth.videoSource || 'live')
                  }
                  data-booth-displayed-source={displayedVideoSource || ''}
                  data-booth-reconnect-attempts={String(booth.reconnectAttempts || 0)}
                  data-booth-fallback-reason={booth.fallbackReason || ''}
                  data-frame-stale={heldFrameStale ? 'true' : 'false'}
                  sx={boothCameraFillSx}
                >
                  {showingOriginalInsteadOfAnnotated ? (
                    <Typography
                      data-testid="showcase-annotated-unavailable"
                      data-displayed-source="original"
                      sx={{
                        position: 'absolute',
                        zIndex: 4,
                        left: 8,
                        right: 8,
                        bottom: 8,
                        bgcolor: 'rgba(15, 23, 42, 0.88)',
                        color: '#fbbf24',
                        fontWeight: 800,
                        fontSize: 14,
                        lineHeight: 1.3,
                        px: 1,
                        py: 0.6,
                        borderRadius: 0.5,
                      }}
                    >
                      {SHOWCASE_ANNOTATED_UNAVAILABLE_ORIGINAL}
                    </Typography>
                  ) : null}
                  <SpatialOperationsLiveCamera
                    status={booth.status}
                    mjpegUrl={booth.mjpegUrl}
                    snapshotUrl={SHOWCASE_BOOTH_STILL}
                    retainPaintedFrame={originalSelected}
                    overlays={overlays}
                    focusZoneId={overlays[0]?.zoneId}
                    cameraLabel="Live evidence"
                    connectingLabel={misStickyAnnotatedSelected ? 'Starting annotated video…' : undefined}
                    onError={booth.reconnect}
                    onFreshFrame={() => {
                      setBoothFreshFrame(true);
                      setHeldFrameStale(false);
                      booth.noteFreshFrame?.();
                    }}
                    onFrameStale={() => {
                      setBoothFreshFrame(false);
                      setHeldFrameStale(true);
                    }}
                    streamRelay={boothStreamRelay}
                  />
                </Box>
              ) : misAnnotatedUnavailable ? (
                <Box
                  data-testid="showcase-annotated-unavailable"
                  sx={{ minHeight: 380, display: 'grid', placeItems: 'center', bgcolor: '#e9eef6' }}
                >
                  <Typography color="text.secondary">Annotated video unavailable.</Typography>
                </Box>
              ) : (
                <StillEvidence
                  src={SHOWCASE_BOOTH_STILL}
                  alt="Monitored area evidence"
                  overlay
                  note={fallback ? undefined : `${SHOWCASE_LIVE_EVIDENCE_UNAVAILABLE}. ${SHOWCASE_CAMERA_STREAM_UNAVAILABLE}.`}
                />
              )}
            </EvidenceFrame>
            <LiveStateCard
              phase={phase}
              liveNow={liveNow}
              boothScope={boothScope}
              siteId={siteId}
              policyId={policyId}
              siteTimeZone={siteTimeZone}
              recentEvidenceEnabled={recentEvidenceEnabled}
            />
          </Box>
        </Box>
      ) : (
        <Box
          data-testid="showcase-forklift-pane"
          data-role="dominant"
          data-focused="true"
          data-processing-proven={SHOWCASE_FORKLIFT_PROCESSING_PROVEN ? 'true' : 'false'}
          data-source-status={forklift.status}
          data-video-count="1"
          data-single-screen="true"
          sx={{
            display: 'grid',
            gridTemplateRows: SHOWCASE_ROOM_B_GRID_ROWS,
            gap: 0.5,
            height: '100%',
            minHeight: 0,
            [SHOWCASE_SHORT_VIEWPORT]: { gap: 0.3, gridTemplateRows: SHOWCASE_ROOM_B_GRID_ROWS_SHORT },
          }}
        >
          <Box
            data-testid="showcase-room-b-dashboard"
            data-summary="removed"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              data-testid="showcase-room-b-video-stage"
              data-video-dominant="true"
              sx={{
                flex: '0 0 auto',
                width: SHOWCASE_ROOM_B_VIDEO_MAX_WIDTH,
                maxWidth: '100%',
              }}
            >
              <Box
                data-testid="showcase-room-b-video-frame"
                data-aspect="16:9"
                data-fit="contain"
                data-width="full"
                data-distort="false"
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: SHOWCASE_ROOM_B_VIDEO_ASPECT,
                  height: 'auto',
                  overflow: 'hidden',
                  bgcolor: '#0c1624',
                  borderRadius: 0.5,
                }}
              >
                {forkliftVideoReady ? (
                  <Box
                    data-overlay-style="cyan-outline"
                    sx={{ ...roomBCameraFillSx, position: 'absolute', inset: 0 }}
                  >
                    <SpatialOperationsLiveCamera
                      status={forklift.status}
                      mjpegUrl={forklift.mjpegUrl}
                      snapshotUrl={SHOWCASE_FORKLIFT_STILL}
                      overlays={forkliftOverlays}
                      focusZoneId={forkliftOverlays[0]?.zoneId}
                      cameraLabel="Recorded evidence"
                      onError={forklift.reconnect}
                    />
                  </Box>
                ) : (
                  <Box sx={{ position: 'absolute', inset: 0 }}>
                    <StillEvidence
                      src={SHOWCASE_FORKLIFT_STILL}
                      alt="Recorded Room B evidence"
                      objectFit="contain"
                    />
                  </Box>
                )}
                <Box
                  data-testid="showcase-room-b-context-header"
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    maxWidth: 'calc(100% - 20px)',
                    px: 0.9,
                    py: 0.45,
                    borderRadius: 0.75,
                    bgcolor: 'rgba(7,16,24,0.62)',
                    pointerEvents: 'none',
                    [SHOWCASE_SHORT_VIEWPORT]: { top: 8, left: 8, py: 0.35, px: 0.75 },
                  }}
                >
                  <Typography
                    data-testid="showcase-room-b-video-heading"
                    sx={{ fontWeight: 750, fontSize: { xs: 14, lg: 15 }, color: '#f8fafc', lineHeight: 1.2 }}
                  >
                    {SHOWCASE_ROOM_B_SECONDARY}
                  </Typography>
                  <ShowcaseBadge label={SHOWCASE_LABEL_RECORDED_FOOTAGE} tone="recorded" testId="showcase-forklift-badge-recorded" />
                  {showProcessedLive ? (
                    <ShowcaseBadge label={SHOWCASE_LABEL_PROCESSED_LIVE} tone="recorded" testId="showcase-forklift-badge-processed-live" />
                  ) : null}
                  {fallback ? <ShowcaseBadge label={SHOWCASE_LABEL_FALLBACK} tone="fallback" testId="showcase-forklift-badge-fallback" /> : null}
                  <Typography data-testid="showcase-room-b-video-secondary" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999 }}>
                    {SHOWCASE_ROOM_B_SECONDARY}
                  </Typography>
                  <Typography data-testid="showcase-forklift-scenario" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', left: -9999 }}>
                    {SHOWCASE_ROOM_B_SCENARIO}
                  </Typography>
                </Box>
                <RoomBRecentEvidence
                  evidence={raeEvidence}
                  incidentType="zone_violation"
                  caption={SHOWCASE_RAE_OBSERVED}
                  testId="showcase-room-b-rae-evidence"
                  imgAlt={SHOWCASE_RAE_OBSERVED}
                  title={showcaseSupportingEvidenceTitle(roomBRae?.recurring?.peak?.label, { recordedReplay: true })}
                  open={openEvidence === 'rae'}
                  onClose={() => setOpenEvidence(null)}
                />
                <RoomBRecentEvidence
                  evidence={proximityEvidence}
                  incidentType="near_miss"
                  caption={SHOWCASE_PROX_OBSERVED}
                  testId="showcase-room-b-prox-evidence"
                  imgAlt={SHOWCASE_PROX_OBSERVED}
                  title={showcaseSupportingEvidenceTitle(roomBProximity?.recurring?.peak?.label, { recordedReplay: true })}
                  open={openEvidence === 'prox'}
                  onClose={() => setOpenEvidence(null)}
                />
              </Box>
            </Box>
          </Box>
          <Box
            data-testid="showcase-room-b-lower"
            data-prox-min-col="1fr"
            data-layout="equal-columns"
            data-simplified="true"
            sx={{
              display: 'grid',
              gridTemplateColumns: SHOWCASE_ROOM_B_LOWER_COLUMNS,
              gap: 0.75,
              minHeight: 0,
              alignItems: 'start',
              [SHOWCASE_SHORT_VIEWPORT]: { gap: 0.5 },
            }}
          >
            <RoomBConditionCard
              testId="showcase-room-b-rae-card"
              title={SHOWCASE_CONDITION_RAE_LABEL}
              titleTestId="showcase-forklift-condition"
              headlineTestId="showcase-repeating-headline"
              scopeCopy={SHOWCASE_RAE_SCOPE_COPY}
              scopeTestId="showcase-room-b-rae-scope"
              condition={roomBRae}
              trust={raeTrust}
              status={status}
              selectedWindowId={selectedWindowId}
              repeatingTestIdPrefix="showcase-repeating"
              lastCompletedTestId="showcase-room-b-last-completed"
              qualitativeTestId="showcase-room-b-qualitative"
              evidenceTestId="showcase-room-b-rae-evidence"
              recordedReplay
              onViewEvidence={() => setOpenEvidence((current) => (current === 'rae' ? null : 'rae'))}
            />
            <RoomBConditionCard
              testId="showcase-room-b-prox-card"
              title={SHOWCASE_CONDITION_PROXIMITY_LABEL}
              titleTestId="showcase-room-b-prox-label"
              scopeCopy={SHOWCASE_PROX_SCOPE_COPY}
              scopeTestId="showcase-room-b-prox-scope"
              condition={roomBProximity}
              trust={proxTrust}
              status={status}
              selectedWindowId={selectedWindowId}
              repeatingTestIdPrefix="showcase-room-b-prox-repeating"
              lastCompletedTestId="showcase-room-b-prox-last-completed"
              qualitativeTestId="showcase-room-b-prox-qualitative"
              evidenceTestId="showcase-room-b-prox-evidence"
              recordedReplay
              onViewEvidence={() => setOpenEvidence((current) => (current === 'prox' ? null : 'prox'))}
            />
            <ShowcaseVerifyFooter />
          </Box>
        </Box>
      )}
    </Box>
  );
}
